import type {IError, TransportLayer} from "../TransportLayer";
import type {
	AccessTokenDetailsDto,
	SignInRequest,
	OrganizationDto,
	SwitchSignInRequest,
	UserDto,
	AuthenticationCodeDetails,
} from "../../generated/api/base";
import {XHRLoader} from "../../utils/loader/XHRLoader";
import {Organization} from "../models/Organization";
import {XyiconFeature} from "../../generated/api/base";
import {NavigationEnum} from "../../Enums";
import {StringUtils} from "../../utils/data/string/StringUtils";
import type {Space} from "../models/Space";
import {FileUtils} from "../../utils/file/FileUtils";
import type {SupportedFontName} from "../state/AppStateTypes";
import {detectCircularDependenciesInFeatureDependencies} from "../state/AppStateConstants";
import {ReportScope, ReportType} from "../../generated/api/reports";
import {EventNameForViewsChangeInLocalStorage, getActiveWorkspaceViewTabFromLocalStorage} from "../../ui/5.0/topbar/ViewTabsCommon";
import {useAppStore} from "../../StateManager";
import {TimeUtils} from "../../utils/TimeUtils";
import {notify} from "../../utils/Notify";
import {NotificationType} from "../../ui/5.0/popup/NotificationV5";
import {Signal} from "../../utils/signal/Signal";
import {DebugInformation} from "../../utils/DebugInformation";

const DebugPortfolioSwitchLogId = "Portfolio Switching";

type FontPromiseType = {
	arrayBuffer: Promise<ArrayBuffer>;
	base64: Promise<string>;
};

//TODO: might need to move this to a better location
interface IHandShakeResult {
	data: AuthenticationCodeDetails | null;
	error?: IError;
}

export class AuthService {
	public pathToRedirect = "";

	private readonly _transport: TransportLayer;

	private _currentlySwitching = false;
	private _currentSwitchPortfolioId = "";
	private _currentSwitchPromise: Promise<void>;
	private _nextPortfolioId = "";
	private _nextSwitchPromise: Promise<void>;
	private _nextSwitchResolve: () => void;

	public signals = {
		authDataUpdated: Signal.create<AccessTokenDetailsDto>(),
	};

	public get portfolioIdAfterPendingSwitches() {
		return this._nextPortfolioId || this._currentSwitchPortfolioId || this._authData.portfolioID;
	}

	constructor(transport: TransportLayer) {
		this._transport = transport;
	}

	public async logout() {
		await this._transport.signalR.stop();

		// If the current access token is still valid, tell the server to delete it - but we aren't very concerned with the result.
		// NOTE: We have to add the auth header here because the access_token will be null by the time the authInterceptorService is invoked.
		const authData = this._authData;

		if (authData.expiresAt && new Date(authData.expiresAt).getTime() > Date.now()) {
			const headers = {
				"Content-Type": "application/json",
				Authorization: `${authData.tokenType} ${authData.accessToken}`,
			};

			try {
				await this._transport.request({
					url: "auth/signout",
					headers: headers,
					method: XHRLoader.METHOD_POST,
				});
			} catch (error) {
				console.warn(error);
			}
		}

		this._transport.appState.app.navigation.goToLogin();
	}

	public loginPassword(user: string, password: string, isOrgSwitch: boolean = false) {
		let result = {
			error: "",
		};

		// eslint-disable-next-line sonarjs/no-try-promise
		try {
			const loginData: SignInRequest = {
				grantType: "password",
				username: user,
				password: password,
				clientID: this.loginConst.clientId,
				clientSecret: this.loginConst.clientSecret,
			};

			return this.login(loginData, isOrgSwitch);
		} catch (error) {
			result.error = this.getErrorMessage(error);
		}

		return result;
	}

	public async loginWithSSO(ssoLoginData: AccessTokenDetailsDto) {
		let result = {
			error: "",
		};
		try {
			this.updateTokens(ssoLoginData);
			await this.loginComplete();
		} catch (error) {
			result.error = this.getErrorMessage(error);
		}

		return result;
	}

	private getErrorMessage(error: any) {
		console.error(error);
		const err = error?.result as IError;

		return err?.ErrorMessage || "Unknown error.";
	}

	private getLoginTokenData(refreshToken: string) {
		const loginData: SignInRequest = {
			grantType: "refresh_token",
			refreshToken: refreshToken,
			clientID: this.loginConst.clientId,
			clientSecret: this.loginConst.clientSecret,
		};

		return loginData;
	}

	private async login(loginData: SignInRequest, isOrgSwitch: boolean = false) {
		const result = {
			permissions: null as {},
			error: "",
		};

		try {
			const signInResult = await this.signIn(loginData);

			this.updateTokens(signInResult);
			await this.loginComplete();
		} catch (error) {
			result.error = this.getErrorMessage(error);
		}

		return result;
	}

	private async signIn(loginData: SignInRequest) {
		const signInResult = await XHRLoader.loadAsync<AccessTokenDetailsDto>({
			url: `${this._transport.apiUrl}/auth/signin`,
			method: XHRLoader.METHOD_POST,
			params: loginData,
			json: true,
		});

		// If there are no portfolios in this organization, the backend sends a "valid" guid full of 0s, like below:
		// In this case, we replace that with an empty string instead, so it's easier to check
		if (signInResult.portfolioID === "00000000-0000-0000-0000-000000000000") {
			signInResult.portfolioID = "";
		}

		return signInResult;
	}

	//TODO: make this private
	public async handShake(): Promise<IHandShakeResult> {
		let result: IHandShakeResult;

		try {
			const {result: res, error} = await this._transport.request<AuthenticationCodeDetails>({
				url: "acc/handshake",
				method: XHRLoader.METHOD_POST,
			});

			result = {
				data: res,
				error,
			};
		} catch (e) {
			result = {
				error: e.message,
				data: null,
			};
		}

		return result;
	}

	private async initOrganizations() {
		const {result: organizationDataArray, error} = await this._transport.get<OrganizationDto[]>({url: "organizations/all"});

		this._transport.appState.organizations = [];
		for (const organizationData of organizationDataArray) {
			const organization = new Organization(organizationData, this._transport.appState);

			this._transport.appState.organizations.push(organization);
		}

		if (!this._transport.appState.organizationIdByAuthData) {
			throw new Error("Organization not found!");
		}
	}

	public async tryAutoLogin(): Promise<AccessTokenDetailsDto | {error: string}> {
		const localCredential: AccessTokenDetailsDto = this._authData;

		if (this.isValidCredential(localCredential)) {
			await this.loginComplete();
			return this._authData;
		} else {
			return {
				error: "Couldn't log in",
			};
		}
	}

	public switchPortfolio(portfolioId: string, force = false) {
		if (!force && portfolioId === this.portfolioIdAfterPendingSwitches) {
			return this._nextSwitchPromise ?? this._currentSwitchPromise;
		}

		useAppStore.getState().setIsPortfolioSwitchRequestInProgress(true);

		this._transport.signalR.listener.closeAllValidationRuleNotifications();
		// Clear immediately when a new switch request is accepted, even if it needs to queue.
		// This prevents showing stale data from the previous portfolio while waiting.
		this._actions.clearPortfolioLists();

		// Only allow one switch request to be ongoing at any time (causes api errors otherwise)
		if (this._currentlySwitching) {
			if (this._currentSwitchPromise && this._currentSwitchPortfolioId === portfolioId) {
				return this._currentSwitchPromise;
			}

			if (this._nextSwitchPromise) {
				this._nextPortfolioId = portfolioId;
				return this._nextSwitchPromise;
			}

			// there is one request currently, save the next portfolioId
			this._nextPortfolioId = portfolioId;
			this._nextSwitchPromise = new Promise<void>((resolve) => {
				this._nextSwitchResolve = resolve;
			});

			return this._nextSwitchPromise;
		} else {
			this._currentSwitchPortfolioId = portfolioId;
			const switchPromise = this.performSwitchPortfolio(portfolioId);
			this._currentSwitchPromise = switchPromise;
			switchPromise.finally(() => {
				if (this._currentSwitchPromise === switchPromise) {
					this._currentSwitchPromise = null;
					this._currentSwitchPortfolioId = "";
				}
			});

			return switchPromise;
		}
	}

	private async performSwitchPortfolio(portfolioId: string) {
		DebugInformation.start(DebugPortfolioSwitchLogId);
		this._currentlySwitching = true;
		this._transport.services.localStorage.set(
			this._transport.appState.actions.getKeyForLocalStorageSelectedPortfolio(this._authData.userID),
			portfolioId,
		);

		try {
			this._transport.signalR.stop();

			const params: SwitchSignInRequest = {
				portfolioID: portfolioId,
			};

			const {result, error} = await this._transport.requestForOrganization<AccessTokenDetailsDto>({
				url: "auth/switch",
				method: XHRLoader.METHOD_POST,
				params,
				json: true,
			});
			this._localStorage.remove(this.loginConst.cookieName);
			// TODO handle status 401
			if (!result) {
				if (error) {
					notify(this._transport.appState.app.notificationContainer, {
						type: NotificationType.Error,
						title: error.ErrorCode.replaceAll("_", " "),
						description: error.ErrorMessage,
					});
				}
				await TimeUtils.wait(5000);
				// this.logout();
				return;
			}

			this.updateTokens(result);
		} catch (e) {
			console.warn(e?.message || e);
			// TODO try again if not successful
		}

		this._currentlySwitching = false;
		DebugInformation.end(DebugPortfolioSwitchLogId);

		// After portfolio switch, signalR needs to be restarted
		this._transport.signalR.start(this._authData.accessToken);

		// Handle the latest switchPortfolio() call that arrived during the api request.
		// Compare against authData (actual active data scope), not URL state, otherwise
		// a fast A -> B -> A sequence can incorrectly skip the queued switch to A.
		if (this._nextPortfolioId && this._nextPortfolioId !== this._authData.portfolioID) {
			const nextPortfolioId = this._nextPortfolioId;
			const nextSwitchResolve = this._nextSwitchResolve;

			this._nextPortfolioId = "";
			this._nextSwitchResolve = null;
			this._nextSwitchPromise = null;

			this.switchPortfolio(nextPortfolioId).then(() => {
				nextSwitchResolve?.();
			});
		} else if (this._nextSwitchPromise) {
			this._nextPortfolioId = "";
			this._nextSwitchResolve?.();
			this._nextSwitchResolve = null;
			this._nextSwitchPromise = null;
		}

		if (this._transport.appState.selectedFeature === XyiconFeature.SpaceEditor && this._transport.appState.currentUIVersion === "5.0") {
			await this._transport.appState.app.transport.services.feature.refreshList(XyiconFeature.Space);

			const spaceList = this._transport.appState.app.transport.appState.actions
				.getList(XyiconFeature.Space)
				.toSorted((a: Space, b: Space) => StringUtils.sortIgnoreCase(a.name, b.name));

			const spaceRefIdMaybe = StringUtils.getSpaceRefIdFromHash(this._transport.appState.app.hash);
			const isSpaceRefIdInList = spaceList.some((spaceItem) => spaceItem.refId === spaceRefIdMaybe);

			if (isSpaceRefIdInList) {
				this._transport.appState.app.transport.appState.app.navigation.goApp({
					mainNav: NavigationEnum.SPACE,
					subNavs: [spaceRefIdMaybe],
				});
			} else {
				this._transport.appState.app.transport.appState.app.navigation.goApp({
					mainNav: NavigationEnum.SPACES,
				});
			}
		}

		const isHome = [NavigationEnum.REPORTS].includes(this._transport.appState.selectedMenu);
		const selectedFeature = this._transport.appState.selectedFeature;
		const localStorageKeyForActiveWorkspaceViews = getActiveWorkspaceViewTabFromLocalStorage(
			this._transport.appState.user?.id,
			this._transport.appState.organizationIdByAuthData,
		);

		if (!isHome && selectedFeature === XyiconFeature.Report && localStorageKeyForActiveWorkspaceViews) {
			dispatchEvent(new CustomEvent(EventNameForViewsChangeInLocalStorage, {detail: {isPortfolioSwitched: true}}));
		}

		// Need to wait couple of seconds to avoid flickering in the report result component between the old and the new report data.
		await TimeUtils.wait(2000);
		useAppStore.getState().setIsPortfolioSwitchRequestInProgress(false);
	}

	public async switchOrganization(organizationId: string, portfolioId?: string) {
		try {
			const params: SwitchSignInRequest = {
				organizationID: organizationId,
			};

			const savedPortfolioId = this._transport.services.localStorage.get(
				this._actions.getKeyForLocalStorageSelectedPortfolio(this._authData.userID, organizationId),
			);
			const portfolioIdToSwitchAfterOrgSwitch: string = portfolioId || (savedPortfolioId ?? "");

			if (portfolioId) {
				params.portfolioID = portfolioId;
			} /* THIS CAUSES 403 Forbidden error, if the user no longer has access to this portfolio!
			else if (savedPortfolioId) {
				params.portfolioID = savedPortfolioId;
			}*/

			this._currentlySwitching = true;

			this._transport.appState.app.setState({isWaiting: true}); // don't change it back... We're reloading the page after organization switch anyway

			const {result, error} = await this._transport.request<AccessTokenDetailsDto>({
				url: "auth/switch",
				method: XHRLoader.METHOD_POST,
				params,
				json: true,
			});

			// HACKY WAY TO ADD SAVED PORTFOLIO ID: https://dev.azure.com/xyicon/SpaceRunner%20V4/_workitems/edit/3690
			// If the portfolioID returned from the backend is different from the saved one, we override it here
			// Should've been done on the BE side...
			if (portfolioIdToSwitchAfterOrgSwitch && result.portfolioID !== portfolioIdToSwitchAfterOrgSwitch) {
				const portfolioMaybe = this._actions.getPortfolioById(portfolioIdToSwitchAfterOrgSwitch);
				if (portfolioMaybe) {
					result.portfolioID = portfolioIdToSwitchAfterOrgSwitch;
				}
			}

			this._currentlySwitching = false;

			this._localStorage.remove(this.loginConst.cookieName);

			if (result) {
				this.updateTokens(result);
				this._transport.signals.switchOrganization.dispatch();
			} else if (error) {
				this._transport.appState.app.setState({isWaiting: false});
				notify(this._transport.appState.app.notificationContainer, {
					type: NotificationType.Error,
					title: error.ErrorCode.replaceAll("_", " "),
					description: error.ErrorMessage,
				});
			}
		} catch (e) {
			console.warn(e);
			// TODO try again if not successful
		}
	}

	private isValidCredential(credential: AccessTokenDetailsDto) {
		let isValid = false;

		if (credential && credential.refreshToken && credential.accessToken && credential.expiresAt) {
			const accessTokenExpiration = new Date(credential.expiresAt);
			if (accessTokenExpiration.getTime() > Date.now()) {
				isValid = true;
			}
		}

		return isValid;
	}

	public saveAuthData(authData: AccessTokenDetailsDto) {
		this._localStorage.set(this.loginConst.cookieName, authData);
		this.signals.authDataUpdated.dispatch(authData);
	}

	private get _authData(): AccessTokenDetailsDto {
		return this._localStorage.get(this.loginConst.cookieName) ?? {};
	}

	private readonly loginComplete = async () => {
		await this.loadPrimaryOrgWideData();

		this._transport.signalR.start(this._authData.accessToken);
		sessionStorage.clear();

		setTimeout(() => {
			this.refreshToken();
		}, this.calculateRefreshDelay());
	};

	private async loadPrimaryOrgWideData() {
		await this._transport.initGeneralSettings();
		// Load users first to be able to determine if user is admin (signin doesn't contain this information)
		await this.loadUsers();

		const hash = location.hash;
		const pageUrl: NavigationEnum = hash?.split("/")[1] as NavigationEnum;

		await Promise.all([this.loadFields(), this.loadTypes(), this.initOrganizations()]);
		// layouts should only be loaded after fields are loaded
		// views should only be loaded after types are loaded
		// eg.: spaceeditorviews refer to types to migrate layers/filters, like removing type-related rules for types that don't exist anymore
		// Organizations should be loader before views, because viewfolderstructure uses the currently used organization id as a key in its data structure

		await Promise.all([this.loadViews(), this.loadReportViews(), this.loadLayouts(), this.loadLists()]);
		this._transport.appState.organization?.syncGlobalViewsWithAvailableViews();
	}

	public loadSecondaryList() {
		// The list loaded here is loaded soon after logging in.
		// It's not required before logging in, but useful to load right after login

		// Xyicon, Catalog are loaded so Settings / Catalog field delete can check if the field is used before delete

		const circles = detectCircularDependenciesInFeatureDependencies();
		if (circles.length > 0) {
			console.warn("Detected circular dependencies. Get Peter to fix this! ", circles);
		}

		const promises: Promise<any>[] = [
			// Org-wide data
			this.loadFonts(),
			this._transport.services.feature.refreshList(XyiconFeature.Dashboard),
			this._transport.services.feature.refreshList(XyiconFeature.XyiconCatalog),
			this._transport.services.feature.refreshList(XyiconFeature.OrganizationDocument),
		];

		if (this._transport.appState.activePortfolioId) {
			promises.push(
				...[
					// Portfolio-wide data
					this._transport.services.feature.refreshList(XyiconFeature.Xyicon),
					this._transport.services.feature.refreshList(XyiconFeature.Boundary),
					this._transport.services.feature.refreshList(XyiconFeature.PortfolioDocument),
				],
			);
		}

		return Promise.all(promises);
	}

	private async loadUsers() {
		await Promise.all([
			this._transport.services.feature.refreshList(XyiconFeature.User, true),
			this._transport.services.feature.refreshList(XyiconFeature.ExternalUser, true),
		]);

		if (!this._transport.appState.user && this._authData.userID) {
			this._transport.appState.user = this._actions.findUser(this._authData.userID);
		}

		const currentUser = this._transport.appState.user;

		if (currentUser) {
			const {result, error} = await this._transport.requestForOrganization<UserDto>({
				url: "currentuser",
				method: XHRLoader.METHOD_GET,
			});

			currentUser.applyData(result);
		} else {
			console.warn(`No user is found with the following id: ${this._authData.userID}`);
			return this.logout();
		}

		// Get removed users list from the backend
		this._transport.appState.removedUsersList = await this._transport.getRemovedUsersList();
	}

	private loadTypes() {
		return this._transport.services.typefield.populateFullList("types");
	}

	private loadFields() {
		return this._transport.services.typefield.populateFullList("fields");
	}

	private loadLayouts() {
		return Promise.all([this._transport.services.typefield.refreshMapping(), this._transport.services.fieldLayout.refreshLayouts()]);
	}

	private async loadFonts() {
		const fontNamesAndFiles: {
			[key in SupportedFontName]: string;
		} = {
			Arial: "Arial-Regular",
			Georgia: "Georgia-Regular",
			OpenSans: "OpenSans-Regular",
			Roboto: "Roboto-Regular",
			Lobster: "Lobster-Regular",
			Caveat: "Caveat-Regular",
			SourceCodePro: "SourceCodePro-Regular",
		};

		const fonts: {
			[key in SupportedFontName]: FontPromiseType;
		} = {
			Arial: {
				arrayBuffer: null,
				base64: null,
			},
			Georgia: {
				arrayBuffer: null,
				base64: null,
			},
			OpenSans: {
				arrayBuffer: null,
				base64: null,
			},
			Roboto: {
				arrayBuffer: null,
				base64: null,
			},
			Lobster: {
				arrayBuffer: null,
				base64: null,
			},
			Caveat: {
				arrayBuffer: null,
				base64: null,
			},
			SourceCodePro: {
				arrayBuffer: null,
				base64: null,
			},
		};

		const fontNames = Object.keys(fontNamesAndFiles) as SupportedFontName[];

		for (const fontName of fontNames) {
			fonts[fontName].arrayBuffer = FileUtils.fetchFileAsArrayBuffer(`src/assets/fonts/${fontName}/${fontNamesAndFiles[fontName]}.ttf`);
		}

		const appState = this._transport.appState;

		for (const fontName of fontNames) {
			appState.fonts[fontName] = {} as unknown as any;
			appState.fonts[fontName].arrayBuffer = await fonts[fontName].arrayBuffer;
			appState.fonts[fontName].base64 = FileUtils.arrayBuffer2Base64(appState.fonts[fontName].arrayBuffer);
		}

		return appState.fonts;
	}

	private loadLists() {
		const lists: Promise<any>[] = [
			this._transport.services.feature.refreshList(XyiconFeature.Portfolio),
			this._transport.services.feature.refreshList(XyiconFeature.PortfolioGroup),
			this._transport.services.feature.refreshList(XyiconFeature.PermissionSet),
			this._transport.services.feature.refreshList(XyiconFeature.UserGroup),
		];

		return Promise.all(lists);
	}

	public loadViews() {
		return this._transport.services.view.loadAllViews();
	}

	public async loadReportViews() {
		const reports = await this._transport.services.feature.getAllReportViews();
		return reports?.filter(
			(item) => (item.definition?.scope ?? ReportScope.CurrentPortfolio) === ReportScope.CurrentPortfolio && item.type !== ReportType.CustomReport,
		);
	}

	private refreshToken = async () => {
		if (this._transport.appState.user) {
			try {
				const loginData = this.getLoginTokenData(this._authData.refreshToken);
				const result = await this.signIn(loginData);

				if (result && result.refreshToken) {
					this.updateTokens(result);

					this._transport.signalR.start(this._authData.accessToken);

					setTimeout(this.refreshToken, this.calculateRefreshDelay());
					console.log("token refreshed");
				} else {
					await this.logout();
				}
			} catch (e) {
				await this.logout();
			}
		}
	};

	private updateTokens(authData: AccessTokenDetailsDto) {
		this.saveAuthData(authData);
		sessionStorage.clear();
	}

	private getRefreshTokenExpiresAt() {
		return this._authData.expiresAt;
	}

	private calculateRefreshDelay() {
		// To disable token refresh:
		// if(1) return 99999999999999999;

		let result = 0.5 * 60 * 1000; // 0.5 minute default

		const refreshTokenAt = this.getRefreshTokenExpiresAt();

		if (refreshTokenAt) {
			const refreshTokenDate = new Date(refreshTokenAt);
			const refreshTokenTime = refreshTokenDate.getTime();

			if (!isNaN(refreshTokenTime)) {
				const diff = refreshTokenTime - new Date().getTime();
				const twoMinutesInMs = 2 * 60 * 1000;

				result = Math.max(10000, diff - twoMinutesInMs);
			}
		}

		console.log(`refreshing in:  ${Math.round(result / 1000)} seconds.`);
		return result;
	}

	public cleanAuth() {
		this._localStorage.remove(this.loginConst.cookieName);
		this._localStorage.remove("sso-config-issue");
		this._localStorage.remove("spotlight-section-expanded");
		this._transport.appState.user = null;
	}

	private get _localStorage() {
		return this._transport.services.localStorage;
	}

	private get _actions() {
		return this._transport.appState.actions;
	}

	public get authData() {
		return this._authData;
	}

	public get loginConst() {
		return this._transport.config.app;
	}
}
